import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, CheckCircle, ChevronRight, Clock, FileCode2, Network as NetworkIcon, Play, Settings, Sparkles, Upload, XCircle } from 'lucide-react'
import { ValidationModal, type ValidationConfig } from '@/components/validation-modal'
import { DiscoveryModal } from '@/components/discovery-modal'
import { PushDiscoveryModal } from '@/components/push-discovery-modal'
import { ConfigurationWorkflowSection, NetworkOverviewSection, SettingsActionsSection, ValidationWorkflowSection, type DiscoveryStatus, type InspectionStatus } from '@/components/network-page-sections'
import { DetailHeader, DetailMetric, DetailPage, DetailTab, DetailTabBar } from '@/components/detail-page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/use-confirm'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/lib/api-client'
import type { Network, Validation } from '@/lib/api-client'

const isValidationInProgress = (status?: string) =>
  status === 'queued' || status === 'pending' || status === 'running'

const getValidationCreatedAtMs = (validation?: Validation | null) => {
  if (!validation?.createdAt) {
    return null
  }

  const ms = new Date(validation.createdAt).getTime()
  return Number.isNaN(ms) ? null : ms
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return 'N/A'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'N/A'
  }

  return date.toLocaleString()
}

const titleCase = (value?: string | null) => {
  if (!value) {
    return 'Unknown'
  }

  return value.charAt(0).toUpperCase() + value.slice(1)
}

export default function NetworkPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { confirm } = useConfirm()
  const { toast } = useToast()
  const [network, setNetwork] = useState<Network | null>(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [showValidationModal, setShowValidationModal] = useState(false)
  const [showDiscoveryModal, setShowDiscoveryModal] = useState(false)
  const [showPushModal, setShowPushModal] = useState(false)
  const [validations, setValidations] = useState<Validation[]>([])
  const [currentValidation, setCurrentValidation] = useState<Validation | null>(null)
  const [isCancellingValidation, setIsCancellingValidation] = useState(false)
  const [discoveryStatus, setDiscoveryStatus] = useState<DiscoveryStatus>(null)
  const [inspectionStatus, setInspectionStatus] = useState<InspectionStatus>(null)
  const [error, setError] = useState<string | null>(null)

  const isDiscoveryRunning = discoveryStatus?.status === 'pending' || discoveryStatus?.status === 'running'
  const isDiscoveryFailed = discoveryStatus?.status === 'error'
    || discoveryStatus?.status === 'failed'
    || discoveryStatus?.status === 'cancelled'
  const isExecutableFromUi = network?.sourceType !== 'direct' && network?.sourceType !== 'local' && network?.sourceType !== 'cli'
  const hasGeneratedArtifacts = discoveryStatus?.status === 'completed'
  const canGenerateConfiguration = network?.sourceType === 'github'
  const hasInspectedSourceConfig = Boolean(
    inspectionStatus?.hasConfig
    || inspectionStatus?.networkYaml
    || inspectionStatus?.invariantsYaml,
  )
  const hasGeneratedConfig = Boolean(
    discoveryStatus?.status === 'completed'
    && (discoveryStatus?.networkYaml || discoveryStatus?.invariantsYaml),
  )
  const hasAnyConfigArtifacts = hasInspectedSourceConfig || hasGeneratedConfig
  const showGenerateAction = Boolean(
    canGenerateConfiguration
    && network
    && !hasAnyConfigArtifacts,
  )

  const activeCandidates = [
    ...(currentValidation && isValidationInProgress(currentValidation.status) ? [currentValidation] : []),
    ...validations.filter((validation) => isValidationInProgress(validation.status)),
  ]
  const newestActiveValidation = activeCandidates.reduce<Validation | null>((latest, candidate) => {
    if (!latest) {
      return candidate
    }

    const latestMs = getValidationCreatedAtMs(latest)
    const candidateMs = getValidationCreatedAtMs(candidate)
    if (candidateMs === null) {
      return latest
    }
    if (latestMs === null || candidateMs > latestMs) {
      return candidate
    }
    return latest
  }, null)

  const newestTerminalValidationMs = validations
    .filter((validation) => !isValidationInProgress(validation.status))
    .reduce<number | null>((latest, validation) => {
      const ms = getValidationCreatedAtMs(validation)
      if (ms === null) {
        return latest
      }
      if (latest === null || ms > latest) {
        return ms
      }
      return latest
    }, null)

  const newestActiveValidationMs = getValidationCreatedAtMs(newestActiveValidation)
  const isStaleActiveValidation = newestActiveValidationMs !== null
    && newestTerminalValidationMs !== null
    && newestActiveValidationMs <= newestTerminalValidationMs
  const activeValidation = isStaleActiveValidation ? null : newestActiveValidation
  const hasActiveValidation = Boolean(activeValidation)
  const activeValidationStatus = activeValidation?.status

  const getValidationStatus = (validation: Validation) => {
    if (validation.status === 'error') {
      return { status: 'error', icon: XCircle, color: 'text-error' }
    }
    if (validation.status === 'running') {
      return { status: 'running', icon: Clock, color: 'text-warning' }
    }
    if (validation.status === 'pending' || validation.status === 'queued') {
      return { status: 'pending', icon: Clock, color: 'text-muted-foreground' }
    }
    if (validation.status === 'completed') {
      if (validation.result === 'failure') {
        return { status: 'failure', icon: XCircle, color: 'text-error' }
      }
      return { status: 'success', icon: CheckCircle, color: 'text-success' }
    }

    return { status: 'pending', icon: Clock, color: 'text-muted-foreground' }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    if (!params.id) {
      return
    }

    void fetchNetwork()
    void fetchValidations()
    void fetchDiscoveryStatus()
  }, [isAuthenticated, params.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeValidation?.id || !isValidationInProgress(activeValidation.status)) {
      return
    }

    const interval = window.setInterval(() => {
      void checkValidationStatus(activeValidation.id)
    }, 5000)

    return () => window.clearInterval(interval)
  }, [activeValidation?.id, activeValidation?.status]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isDiscoveryRunning) {
      return
    }

    const interval = window.setInterval(() => {
      void fetchDiscoveryStatus()
    }, 5000)

    return () => window.clearInterval(interval)
  }, [isDiscoveryRunning]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInspection = async (branch?: string) => {
    try {
      const response = await apiClient.inspectNetworkConfig(params.id!, branch)
      if (response.success && response.data) {
        setInspectionStatus(response.data)
      }
    } catch {
      // Keep the page usable if inspection data is unavailable.
    }
  }

  const fetchNetwork = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.getNetwork(params.id!)

      if (!response.success || !response.data) {
        setError(response.error || 'Failed to load network')
        if (response.error?.includes('404') || response.error?.includes('not found')) {
          navigate('/networks')
        }
        return
      }

      const nextNetwork = response.data.network
      setNetwork(nextNetwork)
      await fetchInspection(nextNetwork.defaultBranch)
    } catch {
      setError('Failed to load network')
    } finally {
      setLoading(false)
    }
  }

  const fetchValidations = async () => {
    try {
      const response = await apiClient.getNetworkValidations(params.id!)
      if (!response.success || !response.data) {
        return
      }

      const nextValidations = [...(response.data.validations || [])].sort((left, right) => {
        const leftMs = getValidationCreatedAtMs(left) ?? 0
        const rightMs = getValidationCreatedAtMs(right) ?? 0
        return rightMs - leftMs
      })

      setValidations(nextValidations)
      const runningValidation = nextValidations.find((validation) => isValidationInProgress(validation.status)) ?? null
      setCurrentValidation(runningValidation)
    } catch {
      // Keep the page shell visible even when validation refresh fails.
    }
  }

  const checkValidationStatus = async (validationId: string) => {
    try {
      const response = await apiClient.getValidation(validationId)
      if (!response.success || !response.data) {
        return
      }

      const nextValidation = response.data.validation
      setCurrentValidation(isValidationInProgress(nextValidation.status) ? nextValidation : null)
      setValidations((current) => current.map((validation) => (
        validation.id === validationId ? nextValidation : validation
      )))

      if (nextValidation.status === 'completed' || nextValidation.status === 'success' || nextValidation.status === 'failure') {
        void fetchNetwork()
      }
    } catch {
      // Ignore transient polling failures.
    }
  }

  const runValidation = async (config: ValidationConfig) => {
    setRunning(true)
    try {
      const validationTypes = config.validationType === 'both'
        ? ['analysis', 'simulation']
        : [config.validationType]

      const response = await apiClient.triggerNetworkValidation(params.id!, {
        validationTypes,
        branch: config.branch || undefined,
        configPath: config.configPath || undefined,
      })

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to start validation')
      }

      const jobId = response.data.job_id
      const nextValidations = response.data.validations
      setShowValidationModal(false)

      if (jobId) {
        if (nextValidations && nextValidations.length === 1) {
          navigate(`/validations/${nextValidations[0].id}`)
        } else {
          navigate(`/jobs/${jobId}`)
        }
        return
      }

      await fetchValidations()
    } catch (failure) {
      const message = failure instanceof Error ? failure.message : 'Failed to start validation'
      toast({
        title: 'Validation Failed',
        description: message,
        variant: 'destructive',
      })
      throw failure
    } finally {
      setRunning(false)
    }
  }

  const cancelActiveValidation = async () => {
    if (!activeValidation) {
      return
    }

    const confirmed = await confirm({
      title: 'Cancel validation',
      description: 'This stops the current validation run for this network.',
      confirmLabel: 'Cancel validation',
      cancelLabel: 'Keep running',
      variant: 'destructive',
    })

    if (!confirmed) {
      return
    }

    setIsCancellingValidation(true)
    try {
      const response = await apiClient.cancelValidation(activeValidation.id)
      if (!response.success) {
        throw new Error(response.error || 'Failed to cancel validation')
      }

      toast({
        title: 'Validation Cancelled',
        description: 'The validation has been cancelled.',
      })
      await fetchValidations()
    } catch (failure) {
      toast({
        title: 'Cancel Failed',
        description: failure instanceof Error ? failure.message : 'Failed to cancel validation. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsCancellingValidation(false)
    }
  }

  const fetchDiscoveryStatus = async () => {
    try {
      const response = await apiClient.getNetworkDiscoveryStatus(params.id!)
      if (response.success && response.data) {
        setDiscoveryStatus(response.data)
      }
    } catch {
      // Discovery context is secondary to the page shell.
    }
  }

  const triggerDiscovery = async () => {
    setRunning(true)
    try {
      const response = await apiClient.triggerNetworkDiscovery(params.id!)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to start discovery')
      }

      setDiscoveryStatus({ status: response.data.status })
      setShowDiscoveryModal(false)
      window.setTimeout(() => {
        void fetchDiscoveryStatus()
      }, 1000)
    } finally {
      setRunning(false)
    }
  }

  const cancelDiscovery = async () => {
    try {
      const response = await apiClient.cancelNetworkDiscovery(params.id!)
      if (!response.success) {
        throw new Error(response.error || 'Failed to cancel discovery')
      }

      setDiscoveryStatus({ status: '' })
      toast({
        title: 'Discovery Cancelled',
        description: 'The discovery process has been cancelled.',
      })
      await fetchDiscoveryStatus()
    } catch {
      toast({
        title: 'Cancel Failed',
        description: 'Failed to cancel discovery. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handlePushDiscovery = async (data: {
    branch: string
    createPR: boolean
    commitMessage: string
  }) => {
    const response = await apiClient.pushNetworkDiscoveryToGitHub(params.id!, data)
    if (!response.success || !response.data) {
      const errorMessage = response.error || 'Failed to push to GitHub'
      toast({
        title: 'Push Failed',
        description: errorMessage,
        variant: 'destructive',
      })
      throw new Error(errorMessage)
    }

    toast({
      title: 'Push Queued',
      description: `Files will be pushed to branch "${data.branch}". Check GitHub for the final result.`,
    })
    setShowPushModal(false)
    window.setTimeout(() => {
      void fetchInspection(network?.defaultBranch)
    }, 3000)
  }

  if (loading) {
    return (
      <DetailPage>
        <Card className="rounded-xl border-border/30 shadow-none">
          <CardContent className="flex min-h-[18rem] flex-col items-center justify-center text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            <p className="mt-3 text-muted-foreground">Loading network...</p>
          </CardContent>
        </Card>
      </DetailPage>
    )
  }

  if (error) {
    return (
      <DetailPage>
        <Card className="rounded-xl border-destructive/40 shadow-none">
          <CardContent className="flex items-center gap-3 pt-6">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <span className="text-destructive">{error}</span>
          </CardContent>
        </Card>
      </DetailPage>
    )
  }

  if (!network) {
    return null
  }

  const githubSource = network.sourceType === 'github'
    ? network.sourceConfig as { owner?: string; name?: string; fullName?: string; private?: boolean }
    : null
  const sourceLabel = githubSource?.fullName || network.name
  const sourceTypeLabel = titleCase(network.sourceType)
  const validationTabCount = validations.length > 99 ? '99+' : String(validations.length)

  return (
    <>
      <DetailPage>
        <DetailHeader
          breadcrumbs={(
            <>
              {githubSource ? (
                <>
                  <NetworkIcon className="h-4 w-4" />
                  <span className="font-medium text-primary">{githubSource.owner || 'repository'}</span>
                  <ChevronRight className="h-4 w-4" />
                  <span className="font-semibold text-foreground">{githubSource.name || network.name}</span>
                  <span className="rounded-full border border-border/50 bg-background px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                    {githubSource.private ? 'Internal' : 'Shared'}
                  </span>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => navigate('/networks')} className="transition-colors hover:text-foreground">
                    Networks
                  </button>
                  <ChevronRight className="h-4 w-4" />
                  <span className="font-medium text-foreground">{network.name}</span>
                </>
              )}
            </>
          )}
          icon={<NetworkIcon className="h-6 w-6 text-primary" />}
          title={network.name}
          description={network.description || 'Review validation history and source details for this network.'}
          actions={(
            <>
              {isExecutableFromUi ? (
                <Button onClick={() => setShowValidationModal(true)} disabled={running || hasActiveValidation}>
                  <Play className="mr-2 h-4 w-4" />
                  {activeValidationStatus === 'queued' || activeValidationStatus === 'pending' || activeValidationStatus === 'running'
                    ? 'Validation Active'
                    : validations.length > 0
                      ? 'Start Validation'
                      : 'Start First Validation'}
                </Button>
              ) : null}
              {(showGenerateAction || hasInspectedSourceConfig || isDiscoveryFailed) ? (
                <Button
                  variant="outline"
                  onClick={() => setShowDiscoveryModal(true)}
                  disabled={running || isDiscoveryRunning}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  {hasInspectedSourceConfig ? 'Refresh Source Files' : isDiscoveryFailed ? 'Retry Generation' : 'Generate Configuration'}
                </Button>
              ) : null}
              {network.sourceType === 'github' && hasGeneratedArtifacts ? (
                <Button variant="outline" onClick={() => setShowPushModal(true)}>
                  <Upload className="mr-2 h-4 w-4" />
                  Push to GitHub
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => navigate(`/networks/${params.id}/settings`)}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Button>
              <Button variant="ghost" onClick={() => navigate('/networks')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Networks
              </Button>
            </>
          )}
          metrics={(
            <>
              <DetailMetric
                label="Source"
                value={sourceTypeLabel}
                detail={sourceLabel}
              />
              <DetailMetric
                label="Validations"
                value={validations.length}
                detail={activeValidation ? 'Validation currently in flight' : 'History available'}
              />
              <DetailMetric
                label="Discovery"
                value={titleCase(discoveryStatus?.status || 'not started')}
                detail={discoveryStatus?.lastDiscoveryAt ? formatDateTime(discoveryStatus.lastDiscoveryAt) : 'No recent discovery run'}
              />
              <DetailMetric
                label="Last Activity"
                value={formatDateTime(network.lastValidatedAt || network.updatedAt)}
                detail={network.active ? 'Active network' : 'Inactive network'}
              />
            </>
          )}
          tabs={(
            <DetailTabBar>
              <DetailTab icon={<FileCode2 className="h-4 w-4" />} label="Config Files" active />
              <DetailTab icon={<Clock className="h-4 w-4" />} label="Validation History" meta={validationTabCount} />
              <DetailTab icon={<Settings className="h-4 w-4" />} label="Invariants" />
            </DetailTabBar>
          )}
        />

        <NetworkOverviewSection
          network={network}
          inspectionStatus={inspectionStatus}
          discoveryStatus={discoveryStatus}
        />

        <ValidationWorkflowSection
          network={network}
          validations={validations}
          activeValidation={activeValidation}
          isCancellingValidation={isCancellingValidation}
          onCancelValidation={cancelActiveValidation}
          onViewValidation={(validationId) => navigate(`/validations/${validationId}`)}
          getValidationStatus={getValidationStatus}
        />

        <ConfigurationWorkflowSection
          network={network}
          inspectionStatus={inspectionStatus}
          discoveryStatus={discoveryStatus}
          isDiscoveryRunning={isDiscoveryRunning}
          isDiscoveryFailed={isDiscoveryFailed}
          running={running}
          onGenerate={() => setShowDiscoveryModal(true)}
          onCancel={cancelDiscovery}
          onPushToGitHub={() => setShowPushModal(true)}
        />

        <SettingsActionsSection
          network={network}
          onOpenSettings={() => navigate(`/networks/${params.id}/settings`)}
        />
      </DetailPage>

      <ValidationModal
        open={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        onSubmit={runValidation}
        repositoryName={network.name}
        quota={undefined}
      />

      <DiscoveryModal
        open={showDiscoveryModal}
        onClose={() => setShowDiscoveryModal(false)}
        onSubmit={triggerDiscovery}
        repositoryName={network.name}
        discoveryStatus={discoveryStatus?.status}
      />

      <PushDiscoveryModal
        open={showPushModal}
        onClose={() => setShowPushModal(false)}
        onSubmit={handlePushDiscovery}
        repositoryName={network.name}
      />
    </>
  )
}
